/**
 * The call-room steps this device has actually observed, for the peer pane's
 * stage line (peerWaiting.ts). Registered as `features/vrs-layout` by
 * reducer.ts; kept here, free of the registry, so the rule can be tested by
 * dispatching the very actions the app dispatches.
 *
 * Only Redux events move it, and only events about the room in front of the
 * person:
 *   - SET_ROOM and CONNECTION_WILL_CONNECT start over. A new room, or a new
 *     connection attempt (first or retry), makes every earlier fact stale, so
 *     the line never keeps reporting a failure the app is recovering from.
 *   - CONFERENCE_* and KICKED_OUT count only for the conference being joined
 *     or joined, and CONNECTION_* only for the current connection, the way
 *     base/conference and base/connection scope their own state. A late
 *     event from a conference that was already replaced changes nothing.
 *   - A failure the app retries on its own (error.recoverable, or Jitsi's
 *     reload overlay on web and reload dialog on native) is "retrying", not
 *     failed: the line must not say "leave and try again" while it retries.
 *   - A destroyed conference (the meeting was ended for everyone) and a kick
 *     are "ended": the call is over, it did not fail to start.
 */
import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_JOIN, KICKED_OUT, SET_ROOM } from '../base/conference/actionTypes';
import { CONNECTION_DISCONNECTED, CONNECTION_ESTABLISHED, CONNECTION_FAILED, CONNECTION_WILL_CONNECT } from '../base/connection/actionTypes';
export const INITIAL_CALL_ROOM_STATE = {
    conference: 'pending',
    connection: 'pending'
};
// JitsiConferenceErrors.CONFERENCE_DESTROYED: the meeting was ended for all.
const CONFERENCE_DESTROYED = 'conference.destroyed';
// Web: the errors AbstractPageReloadOverlay.needsRender reloads the page for
// (isFatalJitsiConferenceError / isFatalJitsiConnectionError in
// base/lib-jitsi-meet/functions.any.ts). Names are the lib-jitsi-meet values,
// kept as strings so this reducer stays free of lib-jitsi-meet.
const WEB_RELOADED_CONFERENCE_ERRORS = new Set([
    'conference.focusDisconnected',
    'conference.focusLeft',
    'conference.iceFailed',
    'conference.offerAnswerFailed',
    'conference.videobridgeNotAvailable'
]);
const WEB_RELOADED_CONNECTION_ERRORS = new Set([
    'connection.droppedError',
    'connection.otherError',
    'connection.serverError'
]);
// Native: overlay/middleware.ts opens the reload dialog for every error whose
// recoverable flag is unset, except these.
const NATIVE_NO_RELOAD_ERRORS = new Set([
    'conference.connectionError.accessDenied',
    CONFERENCE_DESTROYED,
    'conference.connectionError',
    'conference.restarted'
]);
function errorName(error) {
    if (typeof error === 'string') {
        return error;
    }
    const name = error && typeof error === 'object' ? error.name : undefined;
    return typeof name === 'string' ? name : '';
}
function errorRecoverable(error) {
    const recoverable = error && typeof error === 'object'
        ? error.recoverable
        : undefined;
    return typeof recoverable === 'boolean' ? recoverable : undefined;
}
/**
 * Whether the app is already retrying after this failure: another feature
 * claimed it (error.recoverable, set by lobby, room-lock and authentication),
 * or Jitsi's reload overlay (web) / reload dialog (native) will reconnect.
 */
export function isRetriedFailure(error, kind, platform) {
    const recoverable = errorRecoverable(error);
    const name = errorName(error);
    if (recoverable === true) {
        return true;
    }
    if (platform === 'native') {
        return recoverable === undefined && !NATIVE_NO_RELOAD_ERRORS.has(name);
    }
    // The web overlay reloads for its errors whatever the recoverable flag says.
    return (kind === 'connection' ? WEB_RELOADED_CONNECTION_ERRORS : WEB_RELOADED_CONFERENCE_ERRORS).has(name);
}
function withConnection(state, connection) {
    return state.connection === connection ? state : { ...state, connection };
}
function withConference(state, conference, conferenceInstance = state.conferenceInstance) {
    return state.conference === conference && state.conferenceInstance === conferenceInstance
        ? state
        : { ...state, conference, conferenceInstance };
}
/** Whether an action is about another conference than the one being joined or joined. */
function isOtherConference(state, action) {
    return Boolean(state.conferenceInstance) && action.conference !== state.conferenceInstance;
}
/** Whether an action is about another connection than the current one. */
function isOtherConnection(state, action) {
    return Boolean(state.connectionInstance) && action.connection !== state.connectionInstance;
}
// A failure or an end stays on screen when the failed conference is then left
// (base/conference leaves a conference after an unrecoverable failure).
const FINAL_CONFERENCE_STEPS = new Set(['retrying', 'failed', 'ended']);
export function createCallRoomReducer(platform) {
    return function callRoomReducer(state = INITIAL_CALL_ROOM_STATE, action) {
        switch (action.type) {
            case SET_ROOM:
                return state.conference === 'pending' && state.connection === 'pending'
                    && !state.conferenceInstance && !state.connectionInstance
                    ? state
                    : INITIAL_CALL_ROOM_STATE;
            case CONNECTION_WILL_CONNECT:
                return { ...INITIAL_CALL_ROOM_STATE, connectionInstance: action.connection };
            case CONNECTION_DISCONNECTED:
                return isOtherConnection(state, action) ? state : withConnection(state, 'pending');
            case CONNECTION_ESTABLISHED:
                return isOtherConnection(state, action) ? state : withConnection(state, 'established');
            case CONNECTION_FAILED:
                return isOtherConnection(state, action)
                    ? state
                    : withConnection(state, isRetriedFailure(action.error, 'connection', platform) ? 'retrying' : 'failed');
            case CONFERENCE_WILL_JOIN:
                return withConference(state, 'pending', action.conference);
            case CONFERENCE_JOINED:
                return isOtherConference(state, action) ? state : withConference(state, 'joined', action.conference);
            case CONFERENCE_FAILED: {
                if (isOtherConference(state, action)) {
                    return state;
                }
                if (errorName(action.error) === CONFERENCE_DESTROYED) {
                    return withConference(state, 'ended');
                }
                return withConference(state, isRetriedFailure(action.error, 'conference', platform) ? 'retrying' : 'failed');
            }
            case KICKED_OUT:
                return isOtherConference(state, action) ? state : withConference(state, 'ended');
            case CONFERENCE_LEFT:
                return isOtherConference(state, action) || FINAL_CONFERENCE_STEPS.has(state.conference)
                    ? state
                    : withConference(state, 'left');
            default:
                return state;
        }
    };
}
function currentPlatform() {
    return typeof navigator !== 'undefined' && navigator.product === 'ReactNative'
        ? 'native'
        : 'web';
}
export const callRoomReducer = createCallRoomReducer(currentPlatform());
